import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import PurchaserClient from "../PurchaserClient";
import type {
    CatalogFilterParams,
    CatalogItemPayload,
    CatalogItemUpdatePayload,
    AddToRFQPayload,
    CreateRFQFromCatalogPayload,
    CatalogItemHistoryParams,
} from "../Purchaser/ProductCatalog";
import { queryClient, type QueryInvalidateOption } from "./client";
import { invalidateRFQListQueries, invalidateRFQQueries } from "./rfq";

/**
 * Catalog Query Options
 */
export const CatalogQueryOptions = {
    CatalogList(filters?: CatalogFilterParams) {
        return queryOptions({
            queryKey: ["catalogList", filters],
            queryFn: async () => {
                return await PurchaserClient.getCatalogItems(filters);
            },
            refetchOnWindowFocus: false,
            placeholderData: prev => prev,
            staleTime: 5 * 60 * 1000, // 5 minutes
        });
    },
    CatalogItem(id?: number | null) {
        return queryOptions({
            queryKey: ["catalogItem", id],
            queryFn: async () => {
                if (!id) throw new Error("Invalid catalog item ID");
                return await PurchaserClient.getCatalogItem(id);
            },
            enabled: !!id,
            staleTime: 5 * 60 * 1000, // 5 minutes
        });
    },
    CatalogItemHistory(id?: number | null, params?: CatalogItemHistoryParams) {
        return queryOptions({
            queryKey: ["catalogItemHistory", id, params],
            queryFn: async () => {
                if (!id) throw new Error("Invalid catalog item ID");
                return await PurchaserClient.getCatalogItemHistory(id, params);
            },
            enabled: !!id,
            staleTime: 5 * 60 * 1000, // 5 minutes
        });
    },
};

/**
 * Catalog Hooks
 */
export const useCatalogList = (filters?: CatalogFilterParams) => {
    const query = useQuery(CatalogQueryOptions.CatalogList(filters));
    const { data, ...rest } = query;

    return {
        ...rest,
        items: data ?? [],
    };
};

export const useCatalogItem = (id?: number | null) => {
    const query = useQuery(CatalogQueryOptions.CatalogItem(id));
    const { data, refetch, ...rest } = query;

    const item = useMemo(() => data, [data]);
    const refetchItem = useCallback(() => refetch(), [refetch]);

    return { item, refetchItem, ...rest };
};

export const useCatalogItemHistory = (id?: number | null, params?: CatalogItemHistoryParams) => {
    const query = useQuery(CatalogQueryOptions.CatalogItemHistory(id, params));
    const { data, refetch, ...rest } = query;

    const history = useMemo(() => data?.history ?? [], [data]);
    const pricingTrends = useMemo(() => data?.pricingTrends, [data]);
    const catalogItem = useMemo(() => data?.catalogItem, [data]);
    const total = data?.total ?? 0;
    const refetchHistory = useCallback(() => refetch(), [refetch]);

    return { history, pricingTrends, catalogItem, total, refetchHistory, ...rest };
};

/**
 * Catalog Invalidation Functions
 */
export async function invalidateCatalogListQueries(option?: QueryInvalidateOption) {
    const queryKey = ["catalogList"];

    if (option === "Remove") {
        await queryClient.removeQueries({ queryKey });
    } else {
        await queryClient.invalidateQueries({ queryKey });
    }
}

export async function invalidateCatalogItemQueries(id?: number, option?: QueryInvalidateOption) {
    const queryKey: (string | number)[] = ["catalogItem"];
    if (id) queryKey.push(id);

    if (option === "Remove") {
        await queryClient.removeQueries({ queryKey });
    } else {
        await queryClient.invalidateQueries({ queryKey });
    }
}

export async function invalidateCatalogItemHistoryQueries(id?: number, option?: QueryInvalidateOption) {
    const queryKey: (string | number)[] = ["catalogItemHistory"];
    if (id) queryKey.push(id);

    if (option === "Remove") {
        await queryClient.removeQueries({ queryKey });
    } else {
        await queryClient.invalidateQueries({ queryKey });
    }
}

/**
 * Catalog Mutations
 */
export const useCreateCatalogItem = () => {
    const mutation = useMutation({
        mutationFn: async (payload: CatalogItemPayload) => {
            return await PurchaserClient.createCatalogItem(payload);
        },
        async onSuccess() {
            await invalidateCatalogListQueries();
        },
        onError(e) {
            console.error("Error creating catalog item", e);
        },
    });

    return {
        createItem: mutation.mutate,
        createItemAsync: mutation.mutateAsync,
        isCreating: mutation.isPending,
    };
};

export const useUpdateCatalogItem = () => {
    const mutation = useMutation({
        mutationFn: async (params: { id: number; payload: CatalogItemUpdatePayload }) => {
            return await PurchaserClient.updateCatalogItem(params.id, params.payload);
        },
        async onSuccess(_, variables) {
            await invalidateCatalogListQueries();
            await invalidateCatalogItemQueries(variables.id);
        },
        onError(e) {
            console.error("Error updating catalog item", e);
        },
    });

    return {
        updateItem: mutation.mutate,
        updateItemAsync: mutation.mutateAsync,
        isUpdating: mutation.isPending,
    };
};

export const useDeleteCatalogItem = () => {
    const mutation = useMutation({
        mutationFn: async (id: number) => {
            await PurchaserClient.deleteCatalogItem(id);
            return id;
        },
        async onSuccess() {
            await invalidateCatalogListQueries();
        },
        onError(e) {
            console.error("Error deleting catalog item", e);
        },
    });

    return {
        deleteItem: mutation.mutate,
        isDeleting: mutation.isPending,
    };
};

export const useAddCatalogItemLabel = () => {
    const mutation = useMutation({
        mutationFn: async (params: { itemId: number; labelId: number }) => {
            return await PurchaserClient.addCatalogItemLabel(params.itemId, params.labelId);
        },
        async onSuccess(_, variables) {
            await invalidateCatalogListQueries();
            await invalidateCatalogItemQueries(variables.itemId);
        },
        onError(e) {
            console.error("Error adding label to catalog item", e);
        },
    });

    return {
        addLabel: mutation.mutate,
        isAddingLabel: mutation.isPending,
    };
};

export const useRemoveCatalogItemLabel = () => {
    const mutation = useMutation({
        mutationFn: async (params: { itemId: number; labelId: number }) => {
            await PurchaserClient.removeCatalogItemLabel(params.itemId, params.labelId);
        },
        async onSuccess(_, variables) {
            await invalidateCatalogListQueries();
            await invalidateCatalogItemQueries(variables.itemId);
        },
        onError(e) {
            console.error("Error removing label from catalog item", e);
        },
    });

    return {
        removeLabel: mutation.mutate,
        isRemovingLabel: mutation.isPending,
    };
};

export const useAttachFileToCatalogItem = () => {
    const mutation = useMutation({
        mutationFn: async (params: { itemId: number; fileId: number }) => {
            return await PurchaserClient.attachFileToCatalogItem(params.itemId, params.fileId);
        },
        async onSuccess(_, variables) {
            await invalidateCatalogListQueries();
            await invalidateCatalogItemQueries(variables.itemId);
        },
        onError(e) {
            console.error("Error attaching file to catalog item", e);
        },
    });

    return {
        attachFile: mutation.mutate,
        attachFileAsync: mutation.mutateAsync,
        isAttachingFile: mutation.isPending,
    };
};

export const useRemoveFileFromCatalogItem = () => {
    const mutation = useMutation({
        mutationFn: async (params: { itemId: number; fileId: number }) => {
            await PurchaserClient.removeFileFromCatalogItem(params.itemId, params.fileId);
        },
        async onSuccess(_, variables) {
            await invalidateCatalogListQueries();
            await invalidateCatalogItemQueries(variables.itemId);
        },
        onError(e) {
            console.error("Error removing file from catalog item", e);
        },
    });

    return {
        removeFile: mutation.mutate,
        isRemovingFile: mutation.isPending,
    };
};

export const useAddCatalogItemsToRFQ = () => {
    const mutation = useMutation({
        mutationFn: async (params: { rfqPublicId: string; payload: AddToRFQPayload }) => {
            await PurchaserClient.addCatalogItemsToRFQ(params.rfqPublicId, params.payload);
            return params.rfqPublicId;
        },
        async onSuccess(rfqPublicId) {
            await invalidateRFQListQueries();
            await invalidateRFQQueries({ uniquePublicId: rfqPublicId });
        },
        onError(e) {
            console.error("Error adding catalog items to RFQ", e);
        },
    });

    return {
        addToRFQ: mutation.mutate,
        addToRFQAsync: mutation.mutateAsync,
        isAddingToRFQ: mutation.isPending,
    };
};

export const useCreateRFQFromCatalog = () => {
    const mutation = useMutation({
        mutationFn: async (payload: CreateRFQFromCatalogPayload) => {
            return await PurchaserClient.createRFQFromCatalogItems(payload);
        },
        async onSuccess() {
            await invalidateRFQListQueries();
        },
        onError(e) {
            console.error("Error creating RFQ from catalog items", e);
        },
    });

    return {
        createRFQFromCatalog: mutation.mutate,
        createRFQFromCatalogAsync: mutation.mutateAsync,
        isCreatingRFQ: mutation.isPending,
    };
};
